'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import {
  type GardenBed,
  type GardenPlan,
  type PlantPlacement,
  createDefaultPlan,
  createPlacement,
  getPlans,
} from '@/data/gardenPlanner';
import { getToken } from '@/lib/auth';
import { getGardenPlans, saveGardenPlans } from '@/lib/dataSync';
import {
  buildCanonicalPlantLookup,
  listBuiltInCanonicalPlants,
  listCanonicalPlantCatalog,
} from '@/lib/plantCatalog';
import { resolvePlacement } from '@/lib/gardenPlannerPlacement';

type Status =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'success'; bedName: string; bedId: string }
  | { type: 'error'; message: string };

export type PlannerPlacementMetadata = Partial<Omit<PlantPlacement, 'id' | 'plantId' | 'x' | 'y'>>;

function firstPlanWithBed(plans: GardenPlan[]): { planId: string; bedId: string } | null {
  for (const plan of plans) {
    const bed = plan.beds[0];
    if (bed) return { planId: plan.id, bedId: bed.id };
  }
  return null;
}

function replaceBedInPlans(plans: GardenPlan[], planId: string, bed: GardenBed): GardenPlan[] {
  return plans.map(plan => (
    plan.id === planId
      ? {
          ...plan,
          beds: plan.beds.map(candidate => candidate.id === bed.id ? bed : candidate),
          updatedAt: new Date().toISOString(),
        }
      : plan
  ));
}

export default function AddToPlannerBedButton({
  plantId,
  plantName,
  placementMetadata,
  buttonLabel = 'Add to bed',
  buttonClassName,
}: {
  plantId: string;
  plantName: string;
  placementMetadata?: PlannerPlacementMetadata;
  buttonLabel?: string;
  buttonClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState<GardenPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedBedId, setSelectedBedId] = useState('');
  const [status, setStatus] = useState<Status>({ type: 'idle' });
  const [catalogReady, setCatalogReady] = useState(false);
  const [plantLookup, setPlantLookup] = useState(() => buildCanonicalPlantLookup(listBuiltInCanonicalPlants()));

  const selectedPlan = plans.find(plan => plan.id === selectedPlanId) ?? null;
  const selectedBed = selectedPlan?.beds.find(bed => bed.id === selectedBedId) ?? selectedPlan?.beds[0] ?? null;
  const hasPlans = plans.some(plan => plan.beds.length > 0);
  const plannerHref = status.type === 'success'
    ? `/garden?tab=planner&newBed=${status.bedId}`
    : '/garden?tab=planner';

  const bedOptions = useMemo(() => (
    selectedPlan?.beds ?? []
  ), [selectedPlan]);

  useEffect(() => {
    let cancelled = false;
    void listCanonicalPlantCatalog({ includeDraftGenerated: true })
      .then(catalogPlants => {
        if (!cancelled) {
          setPlantLookup(buildCanonicalPlantLookup(catalogPlants));
          setCatalogReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlantLookup(buildCanonicalPlantLookup(listBuiltInCanonicalPlants()));
          setCatalogReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function openPicker() {
    setOpen(true);
    setStatus({ type: 'loading' });

    const loadedPlans = getToken() ? await getGardenPlans() : getPlans();
    const nextPlans = loadedPlans.length > 0 ? loadedPlans : [];
    const firstSelection = firstPlanWithBed(nextPlans);

    setPlans(nextPlans);
    setSelectedPlanId(firstSelection?.planId ?? '');
    setSelectedBedId(firstSelection?.bedId ?? '');
    setStatus({ type: 'idle' });
  }

  async function addToSelectedBed() {
    setStatus({ type: 'loading' });

    let workingPlans = plans;
    let targetPlan = selectedPlan;
    let targetBed = selectedBed;

    if (!targetPlan || !targetBed) {
      const defaultPlan = createDefaultPlan();
      workingPlans = [defaultPlan];
      targetPlan = defaultPlan;
      targetBed = defaultPlan.beds[0] ?? null;
    }

    if (!targetPlan || !targetBed) {
      setStatus({ type: 'error', message: 'Could not create a planner bed.' });
      return;
    }

    const resolved = resolvePlacement(
      targetBed,
      plantLookup,
      plantId,
      targetBed.width * 6,
      targetBed.height * 6,
    );

    if (!resolved) {
      setStatus({ type: 'error', message: 'This bed may need more room for this plant.' });
      return;
    }

    const placement = createPlacement(plantId, resolved.x, resolved.y, {
      plantName,
      ...placementMetadata,
    });
    const updatedBed = {
      ...targetBed,
      plants: [...targetBed.plants, placement],
    };
    const nextPlans = replaceBedInPlans(workingPlans, targetPlan.id, updatedBed);

    await saveGardenPlans(nextPlans);
    setPlans(nextPlans);
    setSelectedPlanId(targetPlan.id);
    setSelectedBedId(updatedBed.id);
    setStatus({ type: 'success', bedName: updatedBed.name, bedId: updatedBed.id });
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => void openPicker()}
        className={buttonClassName ?? 'inline-flex h-9 items-center gap-1.5 rounded-lg border border-sand px-3 font-body text-[13px] font-semibold text-sage transition-colors hover:bg-light-sage'}
      >
        {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#1F271B]/25 px-4 py-6 sm:grid sm:place-items-center">
          <div className="mx-auto mt-16 w-full max-w-md rounded-xl border border-sand bg-white p-4 shadow-[0_24px_60px_rgba(43,53,31,0.22)] sm:mt-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-heading text-[22px] leading-tight text-dark-text">
                  Add to bed
                </h2>
                <p className="mt-1 font-body text-[13px] text-sage">
                  Choose where to place {placementMetadata?.variety ? `${placementMetadata.variety} ${plantName}` : plantName}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setStatus({ type: 'idle' });
                }}
                className="rounded-full px-2 py-1 font-body text-[18px] leading-none text-sage hover:bg-light-sage hover:text-dark-text"
                aria-label="Close add to bed"
              >
                x
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {status.type === 'loading' && (
                <p className="font-body text-[13px] text-sage">Loading your garden beds...</p>
              )}

              {status.type !== 'loading' && hasPlans && (
                <>
                  <label className="grid gap-1">
                    <span className="font-body text-[11px] font-semibold uppercase tracking-wide text-sage">
                      Plan
                    </span>
                    <select
                      value={selectedPlanId}
                      onChange={event => {
                        const nextPlan = plans.find(plan => plan.id === event.target.value) ?? null;
                        setSelectedPlanId(event.target.value);
                        setSelectedBedId(nextPlan?.beds[0]?.id ?? '');
                        setStatus({ type: 'idle' });
                      }}
                      className="h-10 rounded-lg border border-sand bg-white px-3 font-body text-[14px] text-dark-text outline-none focus:border-sage"
                    >
                      {plans.filter(plan => plan.beds.length > 0).map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="grid gap-1">
                    <span className="font-body text-[11px] font-semibold uppercase tracking-wide text-sage">
                      Bed
                    </span>
                    <select
                      value={selectedBedId}
                      onChange={event => {
                        setSelectedBedId(event.target.value);
                        setStatus({ type: 'idle' });
                      }}
                      className="h-10 rounded-lg border border-sand bg-white px-3 font-body text-[14px] text-dark-text outline-none focus:border-sage"
                    >
                      {bedOptions.map(bed => (
                        <option key={bed.id} value={bed.id}>
                          {bed.name} ({bed.width} x {bed.height} ft)
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {status.type !== 'loading' && !hasPlans && (
                <div className="rounded-lg border border-[#D8E3D0] bg-[#F7FAF4] px-3 py-2">
                  <p className="font-body text-[13px] text-sage">
                    No planner beds yet. We can create a default garden plan and bed for you.
                  </p>
                </div>
              )}

              {status.type === 'error' && (
                <p className="rounded-lg border border-[#E8C4C4] bg-[#FDF0F0] px-3 py-2 font-body text-[13px] font-semibold text-[#B85C5C]">
                  {status.message}
                </p>
              )}

              {status.type === 'success' && (
                <div className="rounded-lg border border-[#C9D9BE] bg-[#F4F8EF] px-3 py-2">
                  <p className="font-body text-[13px] font-semibold text-deep-green">
                    Added to {status.bedName}.
                  </p>
                  <Link
                    href={plannerHref}
                    className="mt-1 inline-block font-body text-[12px] font-semibold text-sage underline-offset-2 hover:text-deep-green hover:underline"
                  >
                    Open Garden Planner
                  </Link>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setStatus({ type: 'idle' });
                }}
                className="rounded-lg border border-sand px-3 py-2 font-body text-[13px] font-semibold text-sage hover:bg-light-sage"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void addToSelectedBed()}
                disabled={status.type === 'loading' || !catalogReady}
                className="rounded-lg bg-deep-green px-3 py-2 font-body text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add to this bed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
